import * as React from 'react'
import { Link } from 'react-router-dom'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  ExpandIcon,
  Flex,
  IconButton,
  PinIcon,
  RepeatIcon,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import { usePostData } from '~publish/components/PostCard/PostCardContext'
import { usePostRecurrenceInfo } from '~publish/components/PostCard/usePostRecurrenceInfo'
import { PostNotesEntryPoint } from '~publish/components/PostNotesEntryPoint'
import {
  usePostDetailsDialog,
  PostGroupDialog,
  POST_GROUPS_UI_FLAG,
} from '~publish/components/PostDialogs'
import { useCollaborationNotesPanel } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { PostNotifyBadge } from '~publish/components/PostNotifyBadge'
import { PostStatusBadge } from '~publish/components/PostStatusBadge'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { useTimezone } from '~publish/hooks/useTimezone'

import styles from './CalendarPostCardHeader.module.css'

import type { JSX } from 'react'

// TODO: we have this same component in PostCardHeader, we should move it to a shared component
const TextSeparator = (): JSX.Element => (
  <Text color="subtle" aria-hidden={true}>
    {' • '}
  </Text>
)

const CustomScheduleLabel = (): JSX.Element => {
  return (
    <Text size="sm" color="subtle" className={styles.customLabel}>
      <TextSeparator />
      <Tooltip content="Posting time was set manually and is not determined by the channel’s posting schedule">
        <Flex gap="2xs" align="center">
          <PinIcon size="xsmall" />
          <span>Custom</span>
        </Flex>
      </Tooltip>
    </Text>
  )
}

const RecurringLabel = ({ summary }: { summary: string }): JSX.Element => {
  return (
    <Text size="sm" color="subtle" className={styles.customLabel}>
      <TextSeparator />
      <Tooltip content={summary}>
        <Flex gap="2xs" align="center">
          <RepeatIcon size="xsmall" />
          <span>Recurring</span>
        </Flex>
      </Tooltip>
    </Text>
  )
}

export const CalendarPostCardHeader = (): JSX.Element => {
  const {
    id,
    dueAt,
    isCustomScheduled,
    status,
    notificationStatus,
    schedulingType,
    postGroup,
  } = usePostData()
  const recurrenceInfo = usePostRecurrenceInfo()
  const { showPanel } = useCollaborationNotesPanel()
  const formatDateTime = useDateTimeFormatter()
  const timeZone = useTimezone()

  const { constructDetailsDialogLocation } = usePostDetailsDialog({
    postId: id,
  })

  const detailsDialogLocation = React.useMemo(
    () =>
      constructDetailsDialogLocation({
        cta: 'publish-calendar-postCardHeader-expandButton-1',
      }),
    [constructDetailsDialogLocation],
  )

  // With the group UI on, expanding opens the group-aware dialog in place
  // (no navigation); otherwise it links to the post details route.
  const { isReady, isEnabled } = useSplitEnabled(POST_GROUPS_UI_FLAG)
  const isGroupUiEnabled = isReady && isEnabled
  const [detailsDialogOpen, setDetailsDialogOpen] = React.useState(false)

  return (
    <header className={styles.header}>
      <Flex gap="sm" align="center">
        <Flex gap="2xs" align="center">
          {dueAt ? (
            <Tooltip
              content={
                <Flex direction="column">
                  {timeZone && <span>Channel Local Time:</span>}
                  {formatDateTime(
                    dueAt,
                    timeZone
                      ? {
                          timeZone,
                        }
                      : {},
                  )}
                </Flex>
              }
            >
              <Text size="md" weight="medium">
                {formatDateTime(dueAt, { includeTimeZone: false })}
              </Text>
            </Tooltip>
          ) : (
            <Text size="md" weight="medium">
              No date
            </Text>
          )}
          {isCustomScheduled && <CustomScheduleLabel />}
          {recurrenceInfo && (
            <RecurringLabel summary={recurrenceInfo.summary} />
          )}
        </Flex>
        <PostStatusBadge />
        <PostNotifyBadge
          schedulingType={schedulingType}
          status={status}
          notificationStatus={notificationStatus}
        />
      </Flex>
      <Flex gap="3xs">
        {showPanel && <PostNotesEntryPoint variant="tertiary" size="medium" />}
        {isGroupUiEnabled ? (
          // Controlled so a composer open inside the dialog can close it; the
          // trigger still gets Radix-wired click handling and aria attributes.
          <PostGroupDialog
            postId={id}
            postGroupId={postGroup?.id}
            open={detailsDialogOpen}
            onOpenChange={setDetailsDialogOpen}
            trigger={
              <IconButton label="Expand" size="medium" variant="tertiary">
                <ExpandIcon />
              </IconButton>
            }
          />
        ) : (
          <IconButton
            as={Link}
            to={detailsDialogLocation}
            label="Expand"
            size="medium"
            variant="tertiary"
          >
            <ExpandIcon />
          </IconButton>
        )}
      </Flex>
    </header>
  )
}
